import React, { useEffect } from "react";
import PropTypes from "prop-types";
import _styles from "./SecurityBriefDocument.module.css";
import styles from "./styles";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import {
  Document as DisplayDoc,
  Page as DisplayPage,
} from "react-pdf/dist/entry.webpack";
import { v4 } from "uuid";
import { usePdf } from "../utils/usePdf";

/**
 * View component. Document with pertinent information for security personnel
 * (Key Contacts + Phone Numbers,
 * Key Locations,
 * Local Emergency Numbers,
 * Select Documents).
 */
export function SecurityBriefDocument({
  keyContacts,
  keyLocations,
  selectedDocs,
  onLoad,
}) {
  // Component for rendered base PDF file.
  const FormattedDoc = () => (
    <Document>
      <Page style={styles.page} size="LETTER">
        <View style={styles.header}>
          <Text>Security Brief</Text>
        </View>
        <View style={styles.content}>
          {[
            {
              header: "Key Contacts",
              subHeaders: ["Position", "Name", "Phone Number", "Email"],
              list: keyContacts,
            },
            {
              header: "Key Locations",
              subHeaders: ["Name", "Address 1", "Address 2", "Phone Number"],
              list: keyLocations,
            },
          ].map(({ header, subHeaders, list }) => (
            <View style={styles.content} key={v4()}>
              <Text style={styles["content-header"]}>{header}</Text>
              <View style={styles.flex}>
                {subHeaders.map((heading) => (
                  <Text
                    style={{
                      ...styles["list-item"],
                      ...styles["sub-header"],
                    }}
                    key={v4()}
                  >
                    {heading}
                  </Text>
                ))}
              </View>
              <View style={styles.list}>
                {list.map((item) => (
                  <View
                    style={{
                      ...styles["item-container"],
                      ...styles.light,
                    }}
                    key={v4()}
                  >
                    {Object.values(item).map((value) => (
                      <Text style={styles["list-item"]} key={v4()}>
                        {value}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );

  // Combined PDF with its data.
  const { document, documentObject } = usePdf(
    FormattedDoc(),
    selectedDocs,
    "Security Brief Document"
  );

  // Fire onLoad when documentObject is returned from hook.
  useEffect(() => {
    if (documentObject) {
      onLoad(documentObject);
    }
  }, [documentObject, onLoad]);

  return (
    <div className={_styles.container}>
      {document.file && documentObject && (
        <>
          <a
            href={document.file}
            download="SecurityBriefDocument"
            hidden
            disabled
          >
            Download
          </a>
          <DisplayDoc file={document.file}>
            {document.pages.map((page) => (
              <DisplayPage pageNumber={page + 1} key={v4()} />
            ))}
          </DisplayDoc>
        </>
      )}
    </div>
  );
}

SecurityBriefDocument.propTypes = {
  /**
   * Array of key contact objects.
   */
  keyContacts: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  /**
   * Array of key location objects.
   */
  keyLocations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      phoneNumber: PropTypes.string,
    })
  ),
  /**
   * Array of documents selected.
   */
  selectedDocs: PropTypes.array,
  /**
   * Function that fires when the generated document is loaded.
   * Takes an object with a Blob, ObjectURL, and the Uint8Array as the parameter.
   */
  onLoad: PropTypes.func,
};

SecurityBriefDocument.defaultProps = {
  selectedDocs: [],
};
